// client/src/utils/unitVocabulary.ts
//
// The single source of truth for what counts as a unit of measure on a vendor
// invoice line, plus the OCR repair pass that runs before anything is parsed.
//
// WHY THIS EXISTS
// ---------------
// `normalizeUnit` used to end in a catch-all `return v.toUpperCase()`, so ANY
// run of letters was accepted as a unit. Two things went wrong because of it:
//
//   1. Mistral OCR reads "14OZ" as "140Z" (letter O -> digit zero). The size
//      regex then takes "140" as the number and "Z" as the unit, and "Z" is
//      truthy so it passes every guard. The line is stored as "140 Z".
//   2. Descriptions like "SHRIMP 16-20 EZ PEELED" hand "EZ" to the same regex,
//      and "EZ" becomes a unit too.
//
// Both are now handled here: `repairUnitOcr` undoes the known O/0 misread, and
// `canonicalUnit` rejects anything outside the vocabulary instead of blessing it.
//
// MIRRORED FILE: src/utils/unitVocabulary.js is a line-for-line twin. CRA's
// ModuleScopePlugin forbids importing from src/, so the copy is the only option
// (the repo already does this for several utils). Change one, change both.

/** Outer/selling units — what the grid calls "Pack Unit" (splitUow). */
export const PACK_UNITS = [
  "CASE",
  "PACK",
  "BOX",
  "BAG",
  "TIN",
  "CARTON",
  "DRUM",
  "JAR",
  "BTL",
  "DOZ",
  "PCS",
  "ROLL",
  "TRAY",
  "TUB",
  "PAIL",
  "SACK",
  "BUNDLE",
  // A bale of tissue or towels (John Dickinson). Recognised and UNCLASSIFIED,
  // the D13/D16 shape: owner 2026-10-02 (D89), superseding TECH_DEBT #192's
  // "not a unit". Never converted on the word alone.
  "BALE",
  // RFQ order sheets sell by the set and by the pair; the invoice side sees
  // them too. They arrived with the RFQ canonicalizer this vocabulary
  // replaced (docs/plans/unit-normalizer-unification.md Step 5) - without
  // them a "10 SET" line canonicalises to null and gets defaulted to pieces.
  "SET",
  "PRS",
  // Marine spare-parts and stores units. Added 2026-09-09 after prod's own
  // corpus was read against the source documents: ShipServ RFQs (40% of every
  // RFQ United receives) state LN and SHEET in their UoM column, the extractor's
  // allow-list did not carry them, and every such line stored `uom: null` —
  // 12 of 59 coded lines in the recoverable PDF sample. See
  // `src/__tests__/shipservUomCodeSet.test.js` for the per-line evidence.
  //
  // LENGTH is a COUNTABLE STICK — the 5.5m length pipe is sold in — and NOT the
  // length DIMENSION. `pricingBasis.LENGTH_UNITS` (metres) answers that other
  // question; a row asking for "7 LENGTH" is asking for seven objects.
  //
  // None of the three is classified in SINGLE_ITEM_UNITS / GROUPING_UNITS
  // below, and that is the point of adding them here only: the header of that
  // block says an unclassified unit is safe by construction while a wrongly
  // classified one "moves money on every line quoted in that unit". Whether one
  // LENGTH or one SHEET is one atomic item is the owner's call.
  "LENGTH",
  "SHEET",
  "CUP",
  // USC-BUG-0052: rope, wire and hose are sold by the coil, and the extractor
  // already emits it ("MOORING ROPE ... COIL"). Without it the grid flagged a
  // correct "Coil" Pack Unit as misread, and Confirm & sync refuses any unit
  // missing here (USC-BUG-0058). Unclassified below, like LENGTH and SHEET.
  "COIL",
  // 2026-10-07 retest: "White bread loaf | 20 | LOAF" came out with no unit - the word was in no
  // list, so the line read NEEDS REVIEW and was never priced. A loaf is one piece, so it is
  // classified below with TIN, BTL and ROLL (SINGLE_ITEM_UNITS), not left unclassified like COIL.
  "LOAF",
] as const;

/**
 * Which count units name exactly ONE item, and which name a grouping of
 * unknown size. `PACK_UNITS` above lists BTL beside CASE because both are
 * things an invoice sells BY; this answers the different question the pricing
 * engine has to ask — "if a case holds 12, does one of THESE hold 12 too?"
 *
 * Nothing could ask it before, and the cost was QUO-00010: Sriracha Hot Chili
 * Sauce, TT$895.00 per CASE of 12, quoted at US$137.69 per BOTTLE against
 * US$11.47 due. `pricingBasis.resolveAtomicPerUom` reads a line asking in PCS
 * as holding one atomic unit — true by definition, not an inference — but it
 * was hardcoded to PCS, so the same sentence could not be said about a bottle,
 * and "12/28oz" sitting in the line's own description was read as "a bottle
 * holds 12". A bottle holds one bottle for exactly the reason a piece holds
 * one piece.
 *
 * SINGLE_ITEM units name the container that IS the sellable item. GROUPING
 * units name a pack whose contents are a property of the LINE, never of the
 * word (`quotationGeneration.COUNT_ATOMIC_MULTIPLES` refuses to give CASE a
 * word-level ratio for the same reason).
 *
 * Three deliberate absences, and they are the point of having two sets rather
 * than one predicate and its negation:
 *   - BAG and SACK are genuinely BOTH. A bag of flour is one item; a bag of
 *     ten packets is a grouping, and nothing on a line says which. Unclassified
 *     means every reader keeps the behaviour it has today rather than guessing.
 *   - DOZ and PRS hold a number the WORD fixes (12, 2) — neither one item nor
 *     an unknown grouping. Their NUMBER lives in `WORD_FIXED_COUNTS` below and
 *     `COUNT_ATOMIC_MULTIPLES` prices off it; classifying them here as well
 *     would be a second answer, the duplicate this module exists to prevent.
 *   - SET is a grouping of unknown size, but of unlike things, so a "units per
 *     set" ratio does not mean what a units-per-case ratio means. Left out
 *     until something needs it.
 *
 * Adding a unit to `PACK_UNITS` does NOT require classifying it here — the
 * unclassified case is safe by construction. Classifying one wrongly is not:
 * it moves money on every line quoted in that unit.
 */
export const SINGLE_ITEM_UNITS = new Set(["PCS", "BTL", "JAR", "TIN", "TUB", "PAIL", "DRUM", "ROLL", "LOAF"]);
export const GROUPING_UNITS = new Set(["CASE", "BOX", "CARTON", "PACK", "BUNDLE", "TRAY"]);

/** Inner measure units — what the grid calls "Unit" (splitItemUow). */
// MTR is length rather than mass or volume, but it is a unit lines are
// ordered in (cable, rope, hose) and it has to survive canonicalisation.
//
// PT is the US liquid pint, 0.473176 L. Bare "PT"/"PINT" means the US one
// because that is where the suppliers are — the same choice the quote-side
// conversion table already made — and the IMPERIAL pint, which is 20% larger,
// is NOT in this vocabulary yet. Adding it means deciding how an invoice
// spells it and whether any vendor's bare "PT" should ever mean imperial;
// until then a UK pint would be silently priced as a US one, so nothing here
// pretends to tell them apart.
export const MEASURE_UNITS = ["KG", "G", "MG", "LBS", "OZ", "L", "ML", "GAL", "PT", "PCS", "MTR"] as const;

const VALID_UNITS: ReadonlySet<string> = new Set<string>([...PACK_UNITS, ...MEASURE_UNITS]);

// Spellings seen on real Trinidad supplier invoices, mapped to the canonical form.
// "CSE" is the UNIT MEASURE code Acado (and other T&T suppliers) print for a case.
// CC/KILOGRAM/PIECE/BOTTLES were pulled in from the per-service unit maps this
// vocabulary is replacing (rfq.service, rfqSplit.service): spellings those maps
// already understood, so absorbing them keeps the migration from losing ground.
// docs/plans/unit-normalizer-unification.md.
const UNIT_ALIASES: Record<string, string> = {
  L: "L", LT: "L", LTR: "L", LTRS: "L", LITRE: "L", LITER: "L", LITRES: "L", LITERS: "L",
  ML: "ML", MLS: "ML", CC: "ML", CCS: "ML",
  KG: "KG", KGS: "KG", KILO: "KG", KILOS: "KG", KILOGRAM: "KG", KILOGRAMS: "KG",
  G: "G", GM: "G", GMS: "G", GR: "G", GRM: "G", GRAM: "G", GRAMS: "G",
  MG: "MG",
  LB: "LBS", LBS: "LBS", POUND: "LBS", POUNDS: "LBS",
  // One ounce, the solid one: a fluid ounce is the same ounce (D170.4), so "FL OZ" is OZ too.
  OZ: "OZ", OZS: "OZ", OUNCE: "OZ", OUNCES: "OZ", FLOZ: "OZ", "FL OZ": "OZ",
  GAL: "GAL", GALS: "GAL", GALLON: "GAL", GALLONS: "GAL",
  // The US pint — see MEASURE_UNITS. "PTS" reaches PT through the plural rule
  // below, so it is not listed. No imperial spelling is accepted on purpose:
  // an unrecognised unit is blanked and flagged, which is a question a
  // reviewer can answer, and quietly reading it as 0.473 L is not.
  PT: "PT", PINT: "PT", PINTS: "PT",
  CS: "CASE", CSE: "CASE", CAS: "CASE", CASE: "CASE",
  CTN: "CARTON", CTNS: "CARTON", CARTON: "CARTON",
  PC: "PCS", PCE: "PCS", PCS: "PCS", EA: "PCS", EACH: "PCS", UNIT: "PCS", UNITS: "PCS",
  PIECE: "PCS", PIECES: "PCS",
  // "24 CT" (count) is how US-sourced invoices spell a piece count.
  CT: "PCS", CTS: "PCS", COUNT: "PCS",
  PK: "PACK", PKS: "PACK", PKT: "PACK", PKTS: "PACK", PACK: "PACK", PKG: "PACK", PCK: "PACK",
  BAG: "BAG", SACK: "SACK",
  BOX: "BOX", BX: "BOX",
  TIN: "TIN", CAN: "TIN", CANS: "TIN",
  BTL: "BTL", BOT: "BTL", BOTTLE: "BTL", BOTTLES: "BTL",
  JAR: "JAR", DRUM: "DRUM", PAIL: "PAIL", TUB: "TUB", TRAY: "TRAY", ROLL: "ROLL", ROL: "ROLL",
  DOZ: "DOZ", DZ: "DOZ", DOZEN: "DOZ",
  // LOAFS reaches LOAF through the plural rule below; LOAVES does not (the plural of F is VES).
  LOAF: "LOAF", LOAVES: "LOAF",
  BUNDLE: "BUNDLE", BDL: "BUNDLE", BUN: "BUNDLE", BUNCH: "BUNDLE", BUNCHES: "BUNDLE",
  MTR: "MTR", MTRS: "MTR", METER: "MTR", METERS: "MTR", METRE: "MTR", METRES: "MTR",
  SET: "SET", SETS: "SET",
  PRS: "PRS", PR: "PRS", PAIR: "PRS", PAIRS: "PRS",
  // LENGTH/LENGTHS, SHEET/SHEETS and CUP/CUPS all reach their canonical form
  // through the plural rule below, so only the abbreviation needs listing.
  // Deliberately NOT "LN" — that is a ShipServ PORTAL CODE, and portal codes
  // are translated at the RFQ boundary (`structuredItemsExtraction`'s
  // RFQ_ONLY_ALIASES), never blessed platform-wide. Its sibling `TN` means a
  // TIN to ShipServ and a metric TONNE in UN/CEFACT Rec 20, which is the
  // 1000x reason that boundary exists.
  SHT: "SHEET", SHTS: "SHEET",
};

// Patterns that look like seafood/food size grades, NOT units of measure.
// e.g. "16-20", "26-30", "6-8", "10-12", "2-3", "1-3", "6-8oz", "10-12oz"
export const SIZE_GRADE_RE = /^\d{1,3}-\d{1,3}(oz|lbs|lb|g|kg)?$/i;
// Pack configs like "6x5", "20x8oz", "1x10lbs" are also not UOMs
export const PACK_CONFIG_RE = /^\d+[xX*]\d+(oz|lbs|lb|g|kg)?$/i;

// A container code with its count welded on — "C-12", "CS-24", "BX/6". Vendors
// print these in the unit column as shorthand for "case of twelve", and the
// catalogue stored them verbatim, so a row's unit was a string no conversion
// could ever use and the row was permanently unable to take part in one
// (TECH_DEBT #139: QUO-00010 line 2 priced off a row whose unit was literally
// "C-12", and the customer-facing quote said "C-12").
//
// The separator is REQUIRED and is what makes this safe to run before the
// vocabulary: no real unit contains a hyphen or a slash, so this can never
// swallow one. A bare "C12" is deliberately not matched — that shape is also
// how item codes read, and blanking a unit on a guess is worse than leaving it
// to be flagged.
//
// The name may be up to SIX letters, widened from three on 2026-09-09. The cap
// was arbitrary and it split the rule down the middle: "C-12" and "C-24" were
// read while "BALE-48" and "BALE-30" — the same shape, one letter longer — were
// not, so two live catalogue rows (Snuggle Bath Tissue, Snuggle Ultra Paper
// Towel) threw their pack count away. Found by the read-only prod census in
// `src/scripts/auditCatalogueUnitTokens.js`.
//
// Reading a COUNT off a junk cell and naming its UNIT are different questions.
// #139 answered the count for every letter. The unit is named for exactly two,
// the owner's reading of the codes the prod cleanup found (D89, 2026-10-02):
// HADCO's "C-24" is a CASE of 24 and John Dickinson's "BALE-48" a BALE of 48.
// Any other letter still states only its count.
export const CONTAINER_PACK_CODE_RE = /^[A-Za-z]{1,6}\s*[-/]\s*(\d{1,4})$/;
const PACK_CODE_UNITS: Readonly<Record<string, string>> = Object.freeze({ C: "CASE", BALE: "BALE" });

// A bare number — "24", "1", "02" — where a unit belongs. USC-BUG-0026 (prod):
// Blue Waters prints 24 bottles to a case, the extractor copied that COUNT into
// the unit cell of invoice 3723225747, and because nothing here refused it the
// Master Catalogue row "Blue Waters Regular" was stored, and shown, per "24".
//
// Safe to run before the vocabulary for the container code's reason: no spelling
// this module accepts (PACK_UNITS, MEASURE_UNITS, UNIT_ALIASES) contains a digit,
// so an all-digit cell can never be a real unit. "1" and "0" are no exception —
// `src/scripts/auditCatalogueUnitTokens.js` already lists "101", "1", "02" and
// "44" from prod as junk UNIT tokens.
//
// Refused as a UNIT, deliberately NOT read as a COUNT: `packCountFromUomText`
// below ignores this shape, because `catalogueMatch.packCountFromText` hands it
// every token of free text, where the "160" of "160 French Fries" is a quantity.
// A decimal ("1.5") is not matched either; this is scoped to the count shape the
// ticket and the census show.
export const BARE_NUMBER_UOM_RE = /^\d+$/;

export function isSizeGradeUom(raw: unknown): boolean {
  const v = String(raw || "").trim();
  if (!v) return false;
  return (
    SIZE_GRADE_RE.test(v) ||
    PACK_CONFIG_RE.test(v) ||
    CONTAINER_PACK_CODE_RE.test(v) ||
    BARE_NUMBER_UOM_RE.test(v)
  );
}
/**
 * Whether a unit cell states a NUMBER as well as (or instead of) a unit —
 * "5 KG", "2 CASE", "CASE 10", "CASE OF 10", "4L", "24".
 *
 * The question the Master Catalogue's Unit box asks (QA 2026-09-24: Edit item
 * saved Unit = "5 KG", and the price label read "per 5 KG"). `isSizeGradeUom`
 * above answers a narrower one — which four JUNK SHAPES a vendor prints — and
 * "5 KG" is none of them, so the form and the server both let it through.
 *
 * The rule is the vocabulary's own: no spelling this module accepts contains a
 * digit (see BARE_NUMBER_UOM_RE), so a digit anywhere means the cell is carrying
 * an amount, and an amount belongs in the item's Unit conversions, never in the
 * unit it is priced per. Deliberately NOT folded into `isSizeGradeUom`: the
 * vendor sync asks that one before reading "CASE 10" as a pack notation (MC-4),
 * and widening it would throw that count away.
 *
 * Blank is false — "missing" is the caller's "required" message, not this one.
 */
export function unitStatesNumber(raw: unknown): boolean {
  const v = String(raw == null ? "" : raw).trim();
  return v !== "" && /\d/.test(v);
}
/**
 * What is wrong with a Master Catalogue row's Unit, as the one sentence the
 * item form and the server both show — or null when it is a unit.
 *
 * The Unit box accepts a UNIT and nothing else (QA 2026-09-24, owner: "whether
 * it is xyz, 123, -- or something else from just the units then it should give
 * the error"). A unit is what `isKnownUnit` recognises: the vocabulary above,
 * its aliases ("each", "ctn", "kgs") and plurals. So "xyz", "--", "xyz CASE",
 * "12 KG" and "24" are all refused. A number gets its own sentence, because the
 * fix for "5 KG" is to drop the 5, not to pick another word.
 *
 * Deliberately strict: a real unit this vocabulary has not met is refused too,
 * and the answer is to add it to PACK_UNITS / MEASURE_UNITS / UNIT_ALIASES
 * above (a platform-wide decision: the invoice grid and Confirm & sync read the
 * same list), never to loosen this check. Stored values are not rewritten.
 *
 * Blank is null — "missing" is the caller's "Unit is required", not this.
 */
export function catalogueUnitError(raw: unknown): string | null {
  const unit = String(raw == null ? "" : raw).trim();
  if (!unit) return null;
  if (unitStatesNumber(unit)) {
    return `"${unit}" is not a unit — enter the unit alone, without a number, e.g. EACH, BOX or KG. A pack size goes under Unit conversions.`;
  }
  if (!isKnownUnit(unit)) {
    return `"${unit}" is not a unit — enter a unit such as EACH, BOX, CASE, KG or LTR.`;
  }
  return null;
}
/**
 * The pack count a junk unit column is actually stating, or null.
 *
 * These strings are a real value in the wrong column, and the value is a pack
 * count: "C-12" is a case of twelve, "12x28oz" is twelve 28oz items. Reading it
 * is how a row keeps the fact while losing the fake unit — the alternative is
 * blanking the cell and throwing the number away with it.
 *
 * A SIZE GRADE ("16-20", "26-30") is deliberately NOT read: those are two
 * numbers describing a range of piece sizes, not a count of anything, and
 * "16-20 shrimp" does not mean twenty per pack.
 *
 * Null, never 0 — nothing here is a divisor unless it says so (invariant 8).
 */
export function packCountFromUomText(raw: unknown): number | null {
  const v = String(raw == null ? "" : raw).trim();
  if (!v || SIZE_GRADE_RE.test(v)) return null;
  // A KNOWN container with its count welded on — "CS10PK" (MC-4). Asked first
  // because it names the container too; `readUnitNotation` below explains it.
  const counted = countedContainer(v);
  if (counted) return counted.packCount;
  const container = v.match(CONTAINER_PACK_CODE_RE);
  if (container) {
    const n = Number(container[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const pack = v.match(/^(\d+)[xX*]\d+(oz|lbs|lb|g|kg)?$/i);
  if (pack) {
    const n = Number(pack[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}


/**
 * Resolves `u` to its canonical spelling, or null when it is not a unit we know.
 *
 * Returning null rather than the input is the whole point: it is what stops "Z"
 * and "EZ" being stored as if they were real units.
 */
export function canonicalUnit(u: unknown): string | null {
  const v = String(u ?? "").trim().toUpperCase();
  if (!v) return null;
  if (UNIT_ALIASES[v]) return UNIT_ALIASES[v];
  if (VALID_UNITS.has(v)) return v;
  // Invoices print "CASES"/"BOXES"/"PACKS" freely. Accept a plural whose
  // singular we know — but only after the exact checks, so "LBS" is never
  // stripped to "LB". (The split pass used to default an unrecognised pack unit
  // to the literal "PACKS" and was the other reason this branch existed; it
  // stopped inventing one on 2026-09-08 — ClickUp 86d49xaxm.)
  if (v.endsWith("ES") && VALID_UNITS.has(v.slice(0, -2))) return v.slice(0, -2);
  if (v.endsWith("S") && VALID_UNITS.has(v.slice(0, -1))) return v.slice(0, -1);
  return null;
}

/**
 * Whether two unit spellings name the same unit — through `canonicalUnit`, so
 * CASES and CASE, or LB and LBS, are one. Two spellings the vocabulary does not
 * know are the same only when they are identical; an unknown unit is never
 * equal to a known one. (QUO-00054 / D51: "is this line still in the unit the
 * vessel asked in?" is asked on both sides, and must be asked one way.)
 */
export function isSameUnit(a: unknown, b: unknown): boolean {
  const ca = canonicalUnit(a);
  const cb = canonicalUnit(b);
  if (ca || cb) return ca === cb;
  const ra = String(a == null ? "" : a).trim().toUpperCase();
  return ra !== "" && ra === String(b == null ? "" : b).trim().toUpperCase();
}

/**
 * Is `toUom` a different unit from `fromUom`? The same text, or another
 * spelling of the same unit (`isSameUnit`: CS / case / cases), is not. The text
 * compare comes first because `isSameUnit` never calls two blanks the same unit,
 * and a unit-less line (a price-list order line, its PO line) has not changed
 * unit by staying unit-less. (D82: a PO line's unit against its order line's.)
 */
export function unitChanged(fromUom: unknown, toUom: unknown): boolean {
  return ((fromUom as string) || "") !== ((toUom as string) || "") && !isSameUnit(fromUom, toUom);
}

/**
 * True when `u` is a unit we recognise. Blank is NOT valid here — callers that
 * treat "missing" differently from "wrong" should check emptiness first, since
 * the two want different highlight colours.
 */
export function isKnownUnit(u: unknown): boolean {
  return canonicalUnit(u) !== null;
}

/**
 * Canonicalises a unit for storage. Returns "" for anything unrecognised, so a
 * blank cell (which the grid flags) is the worst case rather than a fake unit.
 */
export function normalizeUnit(u: unknown): string {
  const v = String(u || "").trim();
  if (!v) return "";
  // A size grade is a real value in the wrong column, not a bad unit — blank it
  // before the vocabulary sees it so the UI shows nothing rather than nonsense.
  if (isSizeGradeUom(v)) return "";
  return canonicalUnit(v) || "";
}

// OCR reads the letter O as a zero far more often than the reverse, and "OZ" is
// the only unit where that produces something the parser will still accept.
// Both patterns below are anchored so they cannot touch a genuine number: "1402"
// has no Z, and "0ZX" is not a whole token.
const OZ_JOINED_RE = /(\d)0Z\b/gi; // "140Z"  -> "14OZ"
const OZ_STANDALONE_RE = /\b0Z\b/gi; // "14 0Z" -> "14 OZ"

/**
 * Undoes the known OCR misread before anything is parsed.
 *
 * Reports `repaired` so the caller can mark the line: a silently corrected unit
 * is still a guess, and ops should see an amber cell rather than trust it.
 */
export function repairUnitOcr(text: unknown): { text: string; repaired: boolean } {
  const s = String(text ?? "");
  if (!s) return { text: s, repaired: false };
  const out = s.replace(OZ_JOINED_RE, "$1OZ").replace(OZ_STANDALONE_RE, "OZ");
  return { text: out, repaired: out !== s };
}

/**
 * Pulls the unit out of a "size unit" string such as "14 OZ" or "330ML".
 * The Unit Size column stores the number and the unit together in one string, so
 * validating it means splitting the unit back off.
 *
 * Returns null when there is no unit token at all to judge.
 */
export function unitFromSizeText(text: unknown): string | null {
  const s = String(text ?? "").trim();
  if (!s) return null;
  const m = s.match(/([a-zA-Z]+)\s*$/);
  return m ? m[1].toUpperCase() : null;
}

/**
 * The shape a Unit Size string has to have for the item form to edit it as two
 * separate fields: exactly one number, then exactly one unit. "300g", "1.5 KG",
 * "14 OZ" — and nothing else.
 *
 * Deliberately stricter than `unitFromSizeText` above, which reads the trailing
 * token off ANY string so a reviewer's cell can be flagged. This one answers the
 * different question the Master Catalogue item form asks: "can I put this back
 * in front of a person as a number and a dropdown, and rebuild the exact same
 * string when they save?" A pack config ("12x28oz"), a size grade ("16-20") or a
 * bare unit ("ML") all fail that, so they come back null and the form leaves the
 * stored text ALONE rather than round-tripping it into something else.
 */
const SIZE_TEXT_RE = /^(\d*\.?\d+)\s*([a-zA-Z]+)$/;

/**
 * Split a Unit Size string into the two halves the item form edits separately —
 * `"300g"` -> `{ amount: "300", unit: "G" }`. Null when the text is not exactly
 * a number followed by a unit (see `SIZE_TEXT_RE`).
 *
 * `amount` comes back as the TEXT that was typed, not a number. It goes
 * straight back into a text input, and parsing it here would both add a second
 * `num()` (invariant 8) and round-trip "1.50" as "1.5" — rewriting a person's
 * cell because they opened a modal.
 */
export function splitSizeText(text: unknown): { amount: string; unit: string } | null {
  const m = String(text ?? "").trim().match(SIZE_TEXT_RE);
  return m ? { amount: m[1], unit: m[2].toUpperCase() } : null;
}

// D157.12: a half, a third or a quarter before a unit is that share of one: "MILK 1/2 GAL" is
// 0.5 GAL. "2/5LB" stays two packs of 5 LB, and "1/30lb" one pack of 30 LB, as printed.
const FRACTION_OF_ONE_RE = /(?<![\d.\/])1\s*\/\s*([234])(?![\d.\/])(?=\s*[a-zA-Z])/g;
// D170.4: "16 FL OZ" / "16 FL. OZ" is 16 OZ, the one ounce; joined so the token reads.
const FLUID_OUNCE_RE = /\bFL\.?\s*OZ\b/gi;

/**
 * The first MEASURE a string states — "1.9ltrs" -> `{ amount: "1.9", unit: "L" }`.
 *
 * USC-BUG-0014 (MC-4): the Master Catalogue's size reader kept its own unit
 * regex, so "1.9ltrs", "158gr" and "88lbs" never read and the row printed no
 * Per L / Per kg. Every spelling now goes through `canonicalUnit`, the one list.
 *
 * Scans number+letters tokens and takes the first whose letters are a DIMENSION
 * unit, so "12x300g" reads 300 G and "24 CANS 330ML" reads 330 ML — the same
 * tokens the old regex found. A count word (PCS, CANS) is skipped, never read as
 * a size. `amount` stays TEXT for `splitSizeText`'s reason (invariant 8).
 */
export function measureFromText(text: unknown): { amount: string; unit: string } | null {
  const s = String(text == null ? "" : text).replace(FRACTION_OF_ONE_RE, (_m: string, d: string) => String(1 / Number(d)));
  // "FL OZ" is joined only where nothing else reads: "12 FL OZ 355ML" is 355 ML (D170.4).
  return firstMeasure(s) || firstMeasure(s.replace(FLUID_OUNCE_RE, "FLOZ"));
}

function firstMeasure(s: string): { amount: string; unit: string } | null {
  // Whole words only: "L3" in "QUO-00011 L3" is a code, not 11 litres.
  const tokens = s.match(/\d*\.?\d+\s*[a-zA-Z]+\b/g) || [];
  for (const token of tokens) {
    const size = splitSizeText(token);
    const unit = size ? canonicalUnit(size.unit) : null;
    if (size && unit && isDimensionUnit(unit)) return { amount: size.amount, unit };
  }
  return null;
}

/**
 * The count a unit WORD fixes by itself: twelve to the dozen, two to the pair.
 * Keyed by canonical unit. `quotationGeneration.COUNT_ATOMIC_MULTIPLES` prices
 * DOZ and PRS off these (TECH_DEBT #306), and `readUnitNotation` reads "2DOZ"
 * off them, so there is one twelve. Classifying DOZ/PRS is still NOT done here.
 */
export const WORD_FIXED_COUNTS: Readonly<Record<string, number>> = Object.freeze({ DOZ: 12, PRS: 2 });

// A container word, a count, and optionally what it counts: "CS10PK", "CS10",
// "CS-10", "CASE 10", "CASE OF 10", "BX/6", "CS48TN". The word must be a KNOWN
// grouping unit (checked in `countedContainer`), which is what keeps an item
// code like "C12" or "AB12" unread — the risk CONTAINER_PACK_CODE_RE warns of.
const COUNTED_CONTAINER_RE = /^([A-Za-z]{2,6})(?:\s*[-/]\s*|\s+OF\s+|\s*)(\d{1,4})\s*([A-Za-z]{0,6})$/i;
// A count of a word-fixed unit: "2DOZ", "2 DOZEN".
const COUNT_OF_FIXED_RE = /^(\d{1,4})\s*([A-Za-z]{2,6})$/;
// Inner codes accepted ONLY after a container count, never as units: Albrosco's
// "CS12BT" (bottles) and "CS48TN" (tins), USC-BUG-0029. TN is a tonne elsewhere
// (see UNIT_ALIASES), which is why it is not a unit here; after "a case of 48"
// it can only be what the 48 are.
const INNER_COUNT_CODES = new Set(["BT", "TN"]);
// The same statement written the other way round: "10PK/CS", "12DZ/CS" — the
// count and what it counts, PER container (the docx MC-4 lists it first). The
// slash is what makes it "per case"; without it "24CS" stays ambiguous (#306).
const PER_CONTAINER_RE = /^(\d{1,4})\s*([A-Za-z]{2,6})\s*\/\s*([A-Za-z]{2,6})$/;

// "KG 5", "KG6", "LB 10", "L 4", "KG 2.5" (D100.1).
const MEASURE_THEN_NUMBER_RE = /^([A-Za-z]{1,6})\s*(\d{1,4}(?:\.\d+)?)$/;

function countedContainer(v: string): { unit: string; packCount: number } | null {
  const s = String(v);
  const c = s.match(COUNTED_CONTAINER_RE);
  const p = c ? null : s.match(PER_CONTAINER_RE);
  if (!c && !p) return null;
  const m = c ? c : [s, p![3], p![1], p![2]];
  const unit = canonicalUnit(m[1]);
  if (!unit || !isGroupingUnit(unit)) return null;
  const inner = m[3].toUpperCase();
  // "CS10KG" is a case of ten KILOGRAMS — a size, not ten of anything.
  if (inner && !INNER_COUNT_CODES.has(inner) && (!isKnownUnit(inner) || isDimensionUnit(inner))) {
    return null;
  }
  const n = Number(m[2]);
  if (!(n > 0)) return null;
  // "CS15DZ" is a case of fifteen DOZEN: 180 pieces, the same twelve "2DOZ"
  // reads. Dropping the word stocked 30 eggs where 360 arrived (MC-4 review).
  return { unit, packCount: n * (WORD_FIXED_COUNTS[canonicalUnit(inner) || ""] || 1) };
}

/**
 * What a supplier's UNIT CELL states — the unit, the pack count, or both (MC-4).
 *
 * Meeting 2, 20:08-23:17: Albrosco prints "CS10PK" for a case of ten packs, and
 * the pack unit is what every price conversion hangs off. Readings:
 *   - a known unit            "CS" -> CASE, count null (never guessed: the
 *                              Units / Case cell stays red, O-8)
 *   - a counted container     "CS10PK" / "CASE OF 10" / "CS-10" -> CASE x 10
 *   - a count of a fixed word "2DOZ" -> ONE pack of 24 (O-8, #306)
 *   - a bare number           "24" -> a COUNT with no unit (USC-BUG-0026)
 *   - a pack code the owner read "C-12" -> CASE x 12, "BALE-48" -> BALE x 48 (D89)
 *   - any other container code "X-12", "12x28oz" -> the count, no unit
 * Null when the cell states nothing readable.
 *
 * FOR A UNIT CELL ONLY. Free text goes through `packCountFromUomText`, which
 * reads neither a bare number nor a dozen: "160 French Fries" is a quantity.
 * And never an item NAME — packaging written in a name may hint, never price
 * (D17).
 */
export function readUnitNotation(raw: unknown): { unit: string | null; packCount: number | null; unitSize?: string } | null {
  const v = String(raw == null ? "" : raw).trim();
  if (!v) return null;
  const unit = canonicalUnit(v);
  if (unit) return { unit, packCount: null };
  if (BARE_NUMBER_UOM_RE.test(v)) {
    const n = Number(v);
    return n > 0 ? { unit: null, packCount: n } : null;
  }
  const counted = countedContainer(v);
  if (counted) return counted;
  const code = v.match(CONTAINER_PACK_CODE_RE);
  const codeUnit = code ? PACK_CODE_UNITS[v.replace(/[\s/-].*$/, "").toUpperCase()] : undefined;
  if (code && codeUnit && Number(code[1]) > 0) return { unit: codeUnit, packCount: Number(code[1]) };
  const fixed = v.match(COUNT_OF_FIXED_RE);
  const perWord = fixed ? WORD_FIXED_COUNTS[canonicalUnit(fixed[2]) || ""] : undefined;
  if (fixed && perWord && Number(fixed[1]) > 0) {
    return { unit: "PACK", packCount: Number(fixed[1]) * perWord };
  }
  // D100.1: a measure then a number ("KG 5", "KG6", "LB 10") is ONE PACK of that
  // size, priced per pack. The size rides back as "5 KG" (the Unit Size cell's
  // own shape); the unit is PACK, never the measure and never the digits.
  const sized = v.match(MEASURE_THEN_NUMBER_RE);
  const sizedUnit = sized ? canonicalUnit(sized[1]) : null;
  if (sized && sizedUnit && isDimensionUnit(sizedUnit) && Number(sized[2]) > 0) {
    return { unit: "PACK", packCount: null, unitSize: `${Number(sized[2])} ${sizedUnit}` };
  }
  const n = packCountFromUomText(v);
  return n ? { unit: null, packCount: n } : null;
}

/** True when the unit names exactly one item (a bottle holds one bottle). */
export function isSingleItemUnit(u: unknown): boolean {
  return SINGLE_ITEM_UNITS.has(canonicalUnit(u) || "");
}

/** True when the unit names a pack whose contents the WORD does not fix. */
export function isGroupingUnit(u: unknown): boolean {
  return GROUPING_UNITS.has(canonicalUnit(u) || "");
}

/**
 * D103.6 (owner 2026-10-07: "Carton = case"): true for a CASE or a CARTON. The ONE
 * place that equivalence is written; pricingBasis reads it for both the ratio and
 * the display equivalents, so a carton is never offered as "N per case".
 * Nothing is relabelled - the stored unit stays what it was.
 */
export function isCaseEquivalent(u: unknown): boolean {
  const c = canonicalUnit(u);
  return c === "CASE" || c === "CARTON";
}

// D157.7 (owner 2026-10-19): one item of a product, priced 1:1 against any other of these
// (1 PCS = 1 TIN). Not DRUM; not SINGLE_ITEM_UNITS, whose readers this would move.
const INTERCHANGEABLE_ITEM_UNITS = new Set(["PCS", "BTL", "TIN", "JAR", "TUB", "PAIL", "ROLL", "LOAF"]);
export function isInterchangeableItem(u: unknown): boolean {
  return INTERCHANGEABLE_ITEM_UNITS.has(canonicalUnit(u) || "");
}

/**
 * True when the unit COUNTS things, so a fraction of one cannot be shipped.
 *
 * Restating a line can land on a part pack: 25 PCS of a 10-per-case row is
 * exactly 2.5 CASE, which is arithmetically honest and preserves the line
 * total, and which no vendor will ship. `packConversion.roundQuantity`
 * deliberately does NOT snap it — rounding 2.5 up to 3 overcharges by 20% and
 * sends goods nobody asked for, which is a commercial decision, not a rounding
 * one — so the quotation grid asks this instead and puts the choice, with both
 * totals named, in front of the quoter (TECH_DEBT #150).
 *
 * Mass and volume are continuous and deliberately absent: 2.5 KG is a real
 * thing to buy, and warning about it would train the reader to ignore the
 * warning. So are units this vocabulary does not classify either way (BAG,
 * DOZ, SET) — a wrong "you cannot have half of this" is worse than silence.
 * A caller that only cares about THAT question, and never about what one of
 * them is worth, wants `isWholeCountUnit` below instead.
 */
export function isDiscreteUnit(u: unknown): boolean {
  return isSingleItemUnit(u) || isGroupingUnit(u);
}

/**
 * True when the unit COUNTS things, for the ONE purpose `isDiscreteUnit`
 * above deliberately leaves BAG, DOZ and SET out of: telling a REVIEWER that a
 * fractional count looks wrong. Owner, 2026-09-24, overriding D13 for this
 * purpose only — "nobody sells 2.4 bags or 1.5 dozen."
 *
 * THIS IS NOT THE SAME QUESTION D13 ANSWERED, and that is why it is a second
 * predicate rather than a change to `SINGLE_ITEM_UNITS` / `GROUPING_UNITS` /
 * `isDiscreteUnit` themselves. D13 asked whether a BAG/DOZ/SET is a SINGLE
 * item or a GROUPING of an unknown size — a PRICING question, because
 * `packConversion.piecesPerUnit` reads `isSingleItemUnit` to mean "this unit
 * IS exactly one piece" and derives a per-piece price from it. Folding DOZ in
 * there would price a dozen at the rate of one piece and silently 12x every
 * such line — the QUO-00010 shape this vocabulary exists to prevent. This
 * function is asked a narrower thing — "can a fractional count of this be
 * flagged as probably wrong" — which is true of a bag or a dozen whatever it
 * turns out to hold, and moves no price.
 *
 * Every caller of `isDiscreteUnit` / `isGroupingUnit` / `isSingleItemUnit` /
 * `GROUPING_UNITS` / `SINGLE_ITEM_UNITS` was read before this was added
 * (2026-09-24 audit) and each one resolves a PRICE or a PACK RATIO
 * (`pricingBasis.service.js`, `packConversion.piecesPerUnit`,
 * `supplierOptions.service.js`, `quotationPackStructure.service.js`,
 * `invoiceLineMath.impliedPackCount`, this client's own `costPriceTiers.ts`)
 * or an EXTRACTION/PARSING read (`countedContainer` and `slotTypeIssues` in
 * this file, `packingAlert.service.js` — which already special-cases
 * BAG/SACK of its own accord rather than through `isGroupingUnit`) — never a
 * plain "is this fractional count plausible" question. None of them may
 * widen with BAG/DOZ/SET, so none of them call this function.
 *
 * Used ONLY by the whole-number quotation-line conversion rule — ONE owner
 * since the F7 audit (2026-09-25), `unitConversionGraph.isWholeCountQuantity`
 * / `isFractionalWholeCount` (mirrored `.js`/`.ts`), called from the client's
 * Unit dropdown and catalogue-pick restatement (`quotationUnitConversions.ts`)
 * and from `rfq.service.applyVendorSplitToRfqItems` — and the quotation grid's
 * fractional-quantity flag, the same predicate. SACK is deliberately NOT
 * included — the owner named BAG, DOZ and SET only.
 */
export function isWholeCountUnit(u: unknown): boolean {
  if (isDiscreteUnit(u)) return true;
  const c = canonicalUnit(u);
  return c === "BAG" || c === "DOZ" || c === "SET";
}

const DIMENSION_UNITS = new Set<string>(MEASURE_UNITS.filter((u) => u !== "PCS"));

/** True when the unit measures a dimension — mass, volume or length. */
export function isDimensionUnit(u: unknown): boolean {
  return DIMENSION_UNITS.has(canonicalUnit(u) || "");
}

export type SlotTypeIssues = {
  packUnit: "measure_in_container_slot" | null;
  unitSize: "container_in_measure_slot" | null;
};

/**
 * WHICH SLOT A UNIT BELONGS IN. ClickUp 86d47b59f.
 *
 * The Pack Unit cell and the Unit Size cell accept each other's values, and the
 * grid has no opinion about it. Kaushal on the 2026-08-31 call, 31:48 — "we
 * accept LBS in pack unit as well" — which is how the beef row came to hold a
 * BOX as its unit size, produce no conversion, and store in silence. Deborah's
 * own model, 32:08: "the unit price must match the unit size to the left. That's
 * a compulsory thing."
 *
 * The rule is ASYMMETRIC, and the asymmetry is deliberate. It is NOT "unit size
 * must be a weight": eggs are legitimately 1 CASE = 360 PCE, so a countable is a
 * valid size and a weight-only rule would refuse them.
 *
 * ── IT ONLY FIRES ON POSITIVE EVIDENCE, WHICH IS THE WHOLE DESIGN ────────────
 *
 * The obvious spelling — "pack unit must be in GROUPING_UNITS, or complain" —
 * is wrong here and would have shipped a false positive on the first invoice.
 * BAG is not in GROUPING_UNITS. Neither are DOZ or SET, and the comment on
 * `isDiscreteUnit` says why they are left out on purpose: "a wrong 'you cannot
 * have half of this' is worse than silence". A rule built on absence would
 * therefore flag every BAG line in the corpus as mistyped.
 *
 * So each half asks whether the cell holds something that is POSITIVELY the
 * other kind:
 *
 *   pack unit  — a DIMENSION (KG, LBS, L, MTR...). A weight is not a container.
 *   unit size  — a CONTAINER (CASE, BOX, CARTON...). A box is not a size.
 *
 * An unclassified unit is left alone in both slots, which keeps this change from
 * reclassifying anything. Widening the container set — "is a BAG a container?" —
 * moves money on every row already quoted in BAG and is the owner's call, not
 * this function's (docs/TECH_DEBT.md).
 *
 * PCS is excluded from the dimension set on purpose: "1 PCS" as a pack unit is
 * loose goods, which is a real thing to buy, not a mistyped cell.
 *
 * Pure, and it REPORTS rather than refuses — the caller decides what a mistyped
 * slot costs. Today that is an amber cell on the review grid; the owner has not
 * yet said whether it should block.
 *
 * @returns {{packUnit: string|null, unitSize: string|null}} a short code per
 *   slot, or null where the slot is fine. Prose belongs to whatever renders it.
 */
export function slotTypeIssues(slots: { packUnit?: unknown; unitSize?: unknown }): SlotTypeIssues {
  const s = slots && typeof slots === "object" ? slots : {};
  const sizeUnit = unitFromSizeText(s.unitSize);
  return {
    packUnit: isDimensionUnit(s.packUnit) ? "measure_in_container_slot" : null,
    unitSize: sizeUnit && isGroupingUnit(sizeUnit) ? "container_in_measure_slot" : null,
  };
}
